// Error reporting for the web app (@sentry/react), off unless the api's GET /api/config carries a `sentry` block
// (SENTRY_WEB_DSN on the api). The SDK loads as its own chunk only then, so the app pays nothing without it.
// Sent: the error, its stack, the page path and the signed-in user's id. Not sent: query strings (invite codes,
// Clerk handshakes, signed media links), cookies, headers beyond the user agent, request bodies, emails. No replay.
export type WebSentryConfig = { dsn: string; environment: string; release: string | null };

type Sentry = typeof import("@sentry/react");
let sentry: Sentry | null = null;
let starting: Promise<void> | null = null;
let off = false;
const pending: { error: unknown; extra?: Record<string, unknown> }[] = [];
let userId: () => string | null = () => null;

/** Where the signed-in user's id comes from (main.tsx wires the store); read when an event is sent. */
export function setUserSource(f: () => string | null) {
  userId = f;
}

/** The `sentry` block of /api/config, checked: anything malformed means off. */
export function parseSentryConfig(raw: unknown): WebSentryConfig | null {
  const s = (raw as { sentry?: unknown } | null)?.sentry as Record<string, unknown> | undefined;
  if (!s || typeof s.dsn !== "string" || !/^https:\/\/[^\s]+$/.test(s.dsn.trim())) return null;
  return {
    dsn: s.dsn.trim(),
    environment: typeof s.environment === "string" && s.environment ? s.environment : "production",
    release: typeof s.release === "string" && s.release ? s.release : null,
  };
}

/** Starts Sentry once (a second call is a no-op). With no config, drops anything queued and stays off. */
export function initMonitoring(cfg: WebSentryConfig | null): Promise<void> {
  if (starting) return starting;
  if (!cfg || import.meta.env?.VITE_MOCK === "1") {
    off = true;
    pending.length = 0;
    return (starting = Promise.resolve());
  }
  starting = import("@sentry/react")
    .then((S) => {
      S.init({
        dsn: cfg.dsn,
        environment: cfg.environment,
        release: cfg.release ?? undefined,
        sendDefaultPii: false,
        // the defaults (global handlers, breadcrumbs, dedupe...) and nothing else: no replay, no tracing
        ignoreErrors: ["ResizeObserver loop limit exceeded", "ResizeObserver loop completed with undelivered notifications", /AbortError/, /The operation was aborted/],
        denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-web-extension:\/\//i],
        beforeSend: (e) => {
          const id = userId();
          const ev = scrubEvent(e);
          noteEventId(ev);
          (ev as { user?: unknown }).user = id ? { id } : undefined;
          return ev;
        },
        beforeBreadcrumb: (b) => scrubBreadcrumb(b),
      });
      sentry = S;
      for (const p of pending.splice(0)) send(p.error, p.extra);
    })
    .catch(() => {
      off = true; // a blocked SDK chunk (an ad blocker) must not break the app
      pending.length = 0;
    });
  return starting;
}

function send(error: unknown, extra?: Record<string, unknown>) {
  sentry?.withScope((scope) => {
    if (extra) scope.setExtras(extra);
    sentry?.captureException(error);
  });
}

// v4 (D7): the ids of the last few errors sent, so report_problem can point support at them
const recent: string[] = [];
function noteEventId(e: unknown) {
  const id = (e as { event_id?: unknown } | null)?.event_id;
  if (typeof id !== "string" || !/^[0-9a-f]{32}$/i.test(id)) return;
  recent.push(id);
  if (recent.length > 5) recent.shift();
}
/** The Sentry event ids of the last (up to 5) errors this tab reported, oldest first. */
export const recentErrorIds = (): string[] => [...recent];
/** For tests. */
export const _noteEventId = noteEventId;

/** Reports an error (queued until Sentry has loaded; dropped if it's off). Never throws. */
export function captureError(error: unknown, extra?: Record<string, unknown>) {
  try {
    if (sentry) send(error, extra);
    else if (!off && pending.length < 20) pending.push({ error, extra });
  } catch {
    /* never break the app */
  }
}

/** A lazy chunk that's gone after a deploy: the fix is a reload, not a bug report. */
export function isStaleBuildError(e: unknown): boolean {
  const m = String((e as Error)?.message ?? e ?? "");
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|Unable to preload CSS/i.test(m);
}

// ------------------------------------------------------------------ scrubbing (pure; tested)

const REDACTIONS: [RegExp, string][] = [
  [/\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, "$1 [redacted]"],
  [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g, "[jwt]"],
  [/((?:^|[?&;\s"'])(?:api[_-]?key|key|token|access_token|secret|password|signature|sig|code|session|__clerk_db_jwt|__clerk_handshake)=)[^&\s"';]+/gi, "$1[redacted]"],
];
export function scrubText(s: string): string {
  let out = s;
  for (const [re, rep] of REDACTIONS) out = out.replace(re, rep);
  return out;
}
export const stripQuery = (u: string) => {
  const i = u.search(/[?#]/);
  return i < 0 ? u : u.slice(0, i);
};

type Loose = Record<string, unknown>;

export function scrubBreadcrumb<B>(b: B): B {
  const x = b as Loose & { message?: unknown; data?: Loose };
  if (typeof x.message === "string") x.message = scrubText(x.message);
  if (x.data) {
    for (const k of Object.keys(x.data)) {
      const v = x.data[k];
      if (/header|cookie|body|query/i.test(k)) delete x.data[k];
      else if (typeof v === "string") x.data[k] = /url|from|to|path/i.test(k) ? stripQuery(v) : scrubText(v);
    }
  }
  return b;
}

export function scrubEvent<E>(event: E): E {
  const e = event as Loose & {
    request?: Loose & { url?: string; headers?: Record<string, string> };
    exception?: { values?: { value?: string }[] };
    breadcrumbs?: unknown[];
    message?: string;
    transaction?: string;
  };
  if (e.request) {
    delete e.request.cookies;
    delete e.request.data;
    delete e.request.query_string;
    if (typeof e.request.url === "string") e.request.url = stripQuery(e.request.url);
    if (e.request.headers) {
      const ua = e.request.headers["User-Agent"] ?? e.request.headers["user-agent"];
      e.request.headers = ua ? { "User-Agent": ua } : {};
    }
  }
  if (typeof e.message === "string") e.message = scrubText(e.message);
  if (typeof e.transaction === "string") e.transaction = stripQuery(e.transaction);
  for (const v of e.exception?.values ?? []) if (typeof v.value === "string") v.value = scrubText(v.value);
  for (const b of e.breadcrumbs ?? []) scrubBreadcrumb(b);
  return event;
}
